import { useCallback, useEffect, useState } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { fetchCities, fetchCountries, fetchStates, getPostalCode } from "../common/addressSelect/util";
import './MapLocationPicker.css';
import { isValidAddress, isValidPostalCode } from "../common/validation/fieldRegex";
import { Locale } from "../libraries";

const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY || ""
const containerStyle = {
    height: '400px',
    position: "initial"
};

// Default to Delhi if no location is stored
const defaultCenter = {
    lat: 28.6139,
    lng: 77.2090
};

const countryMapConfig = {
    India: {
        center: { lat: 22.9734, lng: 78.6569 },
        bounds: {
            north: 37.6,
            south: 6.5,
            west: 68.1,
            east: 97.4
        }
    },
    Thailand: {
        center: { lat: 15.8700, lng: 100.9925 },
        bounds: {
            north: 20.5,
            south: 5.6,
            west: 97.3,
            east: 105.6
        }
    },
};

const parseAddressComponents = (components) => {
    const getComponent = (type) =>
        components.find((comp) => comp.types.includes(type))?.long_name || "";

    return {
        countryName: getComponent("country"),
        stateName: getComponent("administrative_area_level_1"),
        cityName: getComponent("locality") || getComponent("sublocality_level_1"),
        postalCode: getComponent("postal_code"),
        area: getComponent("sublocality_level_1"),
        landmark:
            getComponent("point_of_interest") ||
            getComponent("premise") ||
            getComponent("establishment") ||
            getComponent("subpremise"),
    };
};

const MapLocationPicker = ({ addressFormValue, setAddressFormValue, formErrors, setFormErrors }) => {
    const { t } = Locale();
    const [markerPosition, setMarkerPosition] = useState(null);
    const isMapShowOnlyCountryBased = localStorage.getItem('isSection') === 'isBusinessAccount' || localStorage.getItem('isSection') === 'isBusinessBranch';

    // Update marker position when addressFormValue lat/lng changes
    useEffect(() => {
        if (addressFormValue?.lat && addressFormValue?.lng) {
            setMarkerPosition({ lat: addressFormValue?.lat, lng: addressFormValue?.lng });
        } else {
            setMarkerPosition(null);
        }
    }, [addressFormValue?.lat, addressFormValue?.lng]);

    // Try to get stored location from sessionStorage or country map
    const getStoredLocation = () => {
        const countryLatLngMap = {
            India: { lat: 28.6139, lng: 77.2090 },
            Thailand: { lat: 15.8700, lng: 100.9925 },
            USA: { lat: 37.0902, lng: -95.7129 },
            UK: { lat: 55.3781, lng: -3.4360 },
            Canada: { lat: 56.1304, lng: -106.3468 },
        };
        try {
            const location = sessionStorage.getItem('location');
            if (location) {
                const parsed = JSON.parse(location);
                if (parsed.latitude && parsed.longitude) {
                    return {
                        lat: parseFloat(parsed.latitude),
                        lng: parseFloat(parsed.longitude)
                    };
                }
            } else {
                const country = addressFormValue?.country?.name || sessionStorage.getItem('country');
                return countryLatLngMap[country] || null;
            }
        } catch (e) {
            console.error("Error parsing location from sessionStorage", e);
        }
        return null;
    };

    const initialCenter = getStoredLocation() || defaultCenter;

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAP_API_KEY,
        libraries: ["places"]
    });

    const selectedCountryName = addressFormValue?.country?.name;
    const selectedCountry = countryMapConfig[selectedCountryName] || {};

    // Prepare map options including restriction if needed
    const mapOptions = {};
    if (isMapShowOnlyCountryBased) {
        if (selectedCountry.bounds) {
            mapOptions.restriction = {
                latLngBounds: selectedCountry.bounds,
                strictBounds: true
            };
        } else {
            console.warn(`No map bounds configured for country: ${selectedCountryName}`);
        }
    }

    const handleMapClick = useCallback(async (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setMarkerPosition({ lat, lng });

        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAP_API_KEY}`
            );
            const data = await response.json();

            if (data.status === "OK") {
                let postalCode = "";
                let address = "";
                let area = "", landmark = "", cityName = "", stateName = "", countryName = "";

                // 🔍 Filter results to exclude plus codes and prefer human-readable types
                const filteredResults = data.results.filter(
                    (result) =>
                        !result.formatted_address.includes('+') &&
                        (
                            result.types.includes("premise") ||
                            result.types.includes("establishment") ||
                            result.types.includes("street_address") ||
                            result.types.includes("point_of_interest")
                        )
                );

                for (const result of filteredResults) {
                    const components = result.address_components;
                    const parsed = parseAddressComponents(components);

                    if (parsed.postalCode && !postalCode) {
                        postalCode = parsed.postalCode;
                        address = result.formatted_address;
                    }

                    if (parsed?.cityName && !cityName) {
                        cityName = parsed.cityName;
                    }

                    if (!area) area = parsed.area;
                    if (!landmark) landmark = parsed.landmark;
                    if (!stateName) stateName = parsed.stateName;
                    if (!countryName) countryName = parsed.countryName;

                    if (postalCode && cityName) break;
                }

                const countryData = await fetchCountries(countryName);
                const stateData = await fetchStates(countryData?.id, stateName);
                const cityData = await fetchCities(countryData?.id, stateData?.id, cityName);
                const postalCodes = await getPostalCode(cityName, {
                    country: { code: countryData?.iso2 },
                    state: { code: stateData?.state_code }
                });

                const finalPostalCode = postalCodes?.[0]?.value || postalCode || "000000";

                setAddressFormValue((prev) => ({
                    ...prev,
                    country: {
                        id: countryData?.id,
                        name: countryData?.name,
                        code: countryData?.iso2,
                    },
                    state: {
                        id: stateData?.id,
                        name: stateData?.name,
                        state_code: stateData?.state_code,
                    },
                    city: {
                        name: cityData?.name || cityName,
                    },
                    postalCode: {
                        value: postalCode || finalPostalCode,
                    },
                    area,
                    landmark,
                    address,
                    lat,
                    lng
                }));

                // --------------- handle validation 
                let tempError = { ...formErrors };
                tempError = isValidAddress(cityData?.name || cityName, { ...tempError }, 'city', t('field.city'), t);
                tempError = isValidPostalCode(postalCode || finalPostalCode, { ...tempError }, 'postalCode', `${t('field.postalCode')}`, t, countryData?.name);
                tempError = isValidAddress(area, { ...tempError }, 'area', `${t('header.area')}`, t);
                tempError = isValidAddress(address, { ...tempError }, 'address', `${t('field.address')}`, t);
                setFormErrors && setFormErrors(tempError);
            }
        } catch (error) {
            console.error("Error fetching location details:", error);
        }
    }, [setAddressFormValue]);


    if (!isLoaded) return <p>Loading Map...</p>;
    return (
        <div className="pin-location-map-container">
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={markerPosition || initialCenter}
                zoom={markerPosition ? 16 : 10}
                onClick={handleMapClick}
                options={mapOptions}
            >
                {markerPosition && <Marker position={markerPosition} />}
            </GoogleMap>
        </div>
    );
};

export default MapLocationPicker;
